/* eslint-disable max-params */
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { FtoReportState } from '../novelty-check-report-state';
import { FtoTaskStep } from '@/domains/fto-report/enums';
import { Logger, Message } from '@patsnap/tahiti-core';
import { FtoReportTaskStatusModel } from '@/domains/fto-report/models/task-status-model';
import { FtoReportSummaryTechFeaturesService } from '../../summary-tech-features-service';
import { CurrentTaskService } from '@/services/current-task/current-task-service';
import { I18nService } from '@telecom/shared/services/i18n';
import type { FtoReportContext } from '../novelty-check-report-context';

@injectable()
export class FtoReportSummaryStartState implements FtoReportState {
    constructor(
        @inject(Logger)
        private readonly _logger: Logger,
        @inject(FtoReportTaskStatusModel)
        private readonly _noveltyCheckTaskStatusModel: FtoReportTaskStatusModel,
        @inject(CurrentTaskService)
        private readonly _currentTaskService: CurrentTaskService,
        @inject(FtoReportSummaryTechFeaturesService)
        private readonly _summaryTechFeaturesService: FtoReportSummaryTechFeaturesService,
        @inject(Message)
        private readonly _message: Message,
        @inject(I18nService)
        private readonly _i18nService: I18nService,
    ) { }

    getStep(): FtoTaskStep {
        return FtoTaskStep.SUMMARY_START;
    }

    async enter(context: FtoReportContext): Promise<void> {
        this._logger.info('进入<总结用户输入为技术要点>状态');
        this._switchTaskStep(FtoTaskStep.SUMMARY_START);
        const taskId = this._currentTaskService.taskId;
        try {
            const text = await this._summaryTechFeaturesService.pullSummary(taskId);
            this._handleSummaryTechFeaturesEmpty(text);
        } catch (error) {
            const message = this._i18nService.t('fto.errors.summaryTechFeaturesEmpty');
            this._message.warning({ message });
        }
        const state = context.getState(FtoTaskStep.SUMMARY_END);
        await context.transitionTo(state);
    }

    private _handleSummaryTechFeaturesEmpty(text: string) {
        if (!text) {
            throw new Error('总结技术要点失败');
        }
    }

    exit(): Promise<void> {
        this._logger.info('退出<总结用户输入为技术要点>状态');
        return Promise.resolve();
    }

    async handleNext(): Promise<void> {
        this._logger.warn('<总结用户输入为技术要点>状态不支持手动跳转');
    }

    /**
     * 切换任务步骤
     * @param step 目标步骤
     */
    private _switchTaskStep(step: FtoTaskStep) {
        this._noveltyCheckTaskStatusModel.update(state => {
            state.taskStep = step;
            return state;
        });
    }

    handlePrevious(): Promise<void> {
        this._logger.warn('<总结用户输入为技术要点>状态没有前一个状态');
        return Promise.resolve();
    }
}
